import React, { useEffect, useState } from 'react';
import { BracketApi, BracketComeptitionApi, BracketCompetitionHelpers } from './api';
import type { BracketCompetitionProps, BracketProps, CompetitionPlayerBracketProps, CompetitionResultTypeProps, LeagueProps, MyPlayerProps, PlayerBalanceProps, PlayerBracketPickProps, PlayerBracketProps, RoundEventProps } from '../types';
import BracketPlay from './components/BracketPlay';
import { Button, Text, View } from '../Components/Themed';
import { ActivityIndicator, FlatList, Image, Modal } from 'react-native';
import BracketHeader from './components/BracketHeader';
import RoomHome from './components/RoomHome';
import BracketCompetitionActions from './components/BracketCompetitionActions';
import { useColors } from '../constants/useColors';
import { Icons } from '../Components';
import PlayerBracketSelector from './components/PlayerBrackets';
import CompetitionLeaderboard from './components/CompetitionLeaderboard';

type BracketRoomProps = {
    player_id?:string,
    view_mode:'mobile'|'desktop',
    bracket_id?:string,
    insets?: { top:number, bottom:number, right:number, left:number },
    width:number,
    hide_home?:boolean,
    height?:number,
    restrict_competition?:boolean,
    player_balance?:PlayerBalanceProps,
    bracket_competition_id?:string,
    onCompleteBracket?:(player:MyPlayerProps, player_bracket:PlayerBracketProps, bracket_competition?:BracketCompetitionProps, cpb?:CompetitionPlayerBracketProps) => void,
    onManageCompetition:(bracket_competition_id:string) => void,
    onFocusPosition?:(data:{ x:number, y:number, height:number, width:number }) => void,
    onShareCompetition:(bracket_competition:BracketCompetitionProps) => void,
    onRequestAuthenticate: (auth_strategy_id?:string, opt_in_url?:string) => void,
    onNotEnoughBalance:(data:{ bracket_competition:BracketCompetitionProps }) => void,
    onSelectContest?:(round_event:RoundEventProps, bracket:BracketProps, player_bracket_pick?:PlayerBracketPickProps, away_placeholder_pick?:PlayerBracketPickProps, home_placeholder_pick?:PlayerBracketPickProps) => void
    onClose?:() => void,
    onCreateNewCompetition?:(bracket_id?:string) => void
}
const sections = ['header', 'home', 'bracket_selector', 'bracket']
const MAX_WIDTH = 2000

const BracketRoom = ({ player_id, hide_home, restrict_competition, width, height, view_mode, bracket_id, insets, onCompleteBracket, onSelectContest, onCreateNewCompetition, onManageCompetition, bracket_competition_id, player_balance, onRequestAuthenticate, onFocusPosition, onShareCompetition }:BracketRoomProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState<string|undefined>(undefined);
    const [ load_pb, setLoadPb ] = useState<PlayerBracketProps|undefined>(undefined);
    const [ show_competition_actions, setShowCompetitionActions ] = useState(false);
    const [ show_leaderboard, setShowLeaderboard ] = useState<{
        visible:boolean,
        entered_cpbs: CompetitionPlayerBracketProps[],
        entered_pbs: PlayerBracketProps[]
    }>({
        visible: false,
        entered_cpbs:[],
        entered_pbs: []
    });
    let mode = width < 600 ? 'small' : width < 1500 ? 'medium' : 'large'

    const [ play_data, setPlayData ] = useState<{
        player_brackets:PlayerBracketProps[],
        competition_player_brackets:CompetitionPlayerBracketProps[],
    }>({
        competition_player_brackets: [],
        player_brackets: []
    });
    const { player_brackets, competition_player_brackets } = play_data;

    const [ local_balance, setLocalBalance ] = useState<PlayerBalanceProps|undefined>(player_balance);
    const [ player, setPlayer ] = useState<MyPlayerProps|undefined>(undefined);
    const [ bracket_selector, setBracketSelector ] = useState<string|undefined>(undefined);
    const [ static_data, setStaticData ] = useState<{
        static_loaded:boolean,
        leagues:LeagueProps[],
        competition_result_types:CompetitionResultTypeProps[]
    }>({
        static_loaded: false,
        leagues:[],
        competition_result_types:[]
    });
    const { competition_result_types, leagues, static_loaded } = static_data;

    const [ nav_data, setNavData ] = useState<{
        active_nav: string,
        active_competition?:BracketCompetitionProps,
        active_bracket_id?:string,
        active_player_bracket_id?:string
    }>({
        active_nav: 'home'
    });

    const { active_nav, active_bracket_id, active_competition, active_player_bracket_id } = nav_data;
    const active_player_bracket = player_brackets.find(pb => pb.player_bracket_id == active_player_bracket_id);
    const allow_enter = BracketCompetitionHelpers.canEnter(active_competition) && !BracketCompetitionHelpers.isEntered(competition_player_brackets.find(cpb => cpb.status != 'deleted' && cpb.player_bracket_id == active_player_bracket_id && cpb.bracket_competition_id == active_competition?.bracket_competition_id));

    useEffect(() => {
        if(!load_pb){ return }
        setPlayData({
            ...play_data,
            player_brackets:player_brackets.filter(pb => pb.player_bracket_id != load_pb.player_bracket_id).concat(load_pb)
        })
    },[load_pb])

    useEffect(() => {
        if(!active_player_bracket){ return }
        if(!active_competition){ return }
        if(active_player_bracket.completion_pct != 1){ return }
        if(show_competition_actions){ return }
        let entered = competition_player_brackets.find(cpb => cpb.bracket_competition_id == active_competition.bracket_competition_id && cpb.status != 'deleted');
        if(entered){ return }
        setShowCompetitionActions(true);
    },[active_player_bracket])
    
    let comp_action_width = mode != 'small' && active_bracket_id ? 300 : 0
    let section_width = width - comp_action_width > MAX_WIDTH ? MAX_WIDTH : width - comp_action_width

    //Very First Time we Launch - do this
    useEffect(() => {
        BracketApi.setEnvironment();
        getStaticData()
    },[]);

    useEffect(() => {
        if(!player_balance){return}
        setLocalBalance(player_balance);
    },[player_balance])


    useEffect(() => {
        if(!static_loaded){ return }
        if(!player_id){ return }
        getMyData()
    },[static_loaded, player_id]);

    useEffect(() => {
        if(!bracket_id && !bracket_competition_id){ return }
        if(!static_loaded){ return }
        setUpInitData(bracket_id, bracket_competition_id)
    },[static_loaded, player_id, bracket_id, bracket_competition_id])

    const setUpInitData = async(bracket_id?:string, bracket_competition_id?:string) => {
        let init_bracket_id = bracket_id
        let bc:BracketCompetitionProps | undefined = undefined
        let active_pb_id:string|undefined = undefined
        if(bracket_competition_id){
            let resp = await BracketComeptitionApi.getBracketCompetitionById(bracket_competition_id);
            bc = resp?.bracket_competition
            if(bc){ init_bracket_id = bc.bracket_id }
        }

        if(player_id && init_bracket_id){
            //Get the play data for whaat is provided!!
            const my_data = await BracketComeptitionApi.getMyPlayerBrackets(init_bracket_id);
            console.log(my_data)
            //Try to find an already entered bracket
            active_pb_id = my_data.competition_player_brackets.find(cpb => cpb.bracket_competition_id == bracket_competition_id)?.player_bracket_id
            if(!active_pb_id){
                //Pick a random one
                active_pb_id = my_data.player_brackets[0]?.player_bracket_id
            }
            setPlayData({
                ...play_data,
                player_brackets: my_data.player_brackets,
                competition_player_brackets: my_data.competition_player_brackets,
            })
        }
        setNavData({ 
            ...nav_data, 
            active_nav: 'bracket', 
            active_player_bracket_id:active_pb_id, 
            active_bracket_id:init_bracket_id, 
            active_competition:bc 
        })
        if(view_mode == 'mobile'){ setShowCompetitionActions(true) }
    }

    const getMyData = async() => {
        const pb = await BracketComeptitionApi.getMyBalance();
        const pl = await BracketComeptitionApi.getMyDetails();
        setLocalBalance(pb);
        setPlayer(pl);
    }

    const getStaticData = async() => {
        setLoading('Setting Up Bracket')
        const comp_options = await BracketComeptitionApi.getCompetitionOptions();
        const lgs = await BracketApi.getLeagues();
        setStaticData({
            ...static_data,
            static_loaded: true,
            leagues:lgs,
            competition_result_types: comp_options.competition_result_types
        });
        setTimeout(() => setLoading(undefined),500)
    }

    const handleView = async(bracket_id:string, player_bracket_id?:string, bracket_competition?:BracketCompetitionProps) => {
        setNavData({
            ...nav_data,
            active_nav:'bracket',
            active_player_bracket_id:player_bracket_id,
            active_bracket_id: bracket_id,
            active_competition: bracket_competition
        });
        const my_data = await BracketComeptitionApi.getMyPlayerBrackets(bracket_id);
        setPlayData({
            ...play_data,
            player_brackets: my_data.player_brackets,
            competition_player_brackets: my_data.competition_player_brackets
        });

    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(hide_home){ return <></> }
                return (
                    <BracketHeader
                        view_mode={view_mode}
                        active_nav={active_nav}
                        bracket={undefined}
                        bracket_competition={undefined}
                        onGoHome={() => setNavData({
                            ...nav_data,
                            active_bracket_id:undefined,
                            active_nav:'home',
                            active_competition: undefined,
                            active_player_bracket_id: undefined
                        })}
                    />
                )
            case 'home': 
                if(!static_loaded){ return <></> }
                return (
                    <RoomHome
                        player={player}
                        visible={active_nav == 'home' ? true : false}
                        width={section_width}
                        insets={insets}
                        onFocusPosition={onFocusPosition}
                        onManageCompetition={onManageCompetition}
                        onCreateNewCompetition={onCreateNewCompetition}
                        view_mode={view_mode}
                        onRequestAuthenticate={onRequestAuthenticate}
                        leagues={leagues}
                        onView={handleView}
                    />
                )
            case 'bracket':
                if(active_nav != 'bracket'){ return <></> }
                if(!active_bracket_id){ return <></> }
                return (
                        <BracketPlay
                            width={section_width}
                            view_mode={view_mode}
                            player={player}
                            onSelectContest={onSelectContest}
                            insets={insets}
                            onRequestAuthenticate={() => onRequestAuthenticate(active_competition?.auth_strategy_id, active_competition?.opt_in_url)}
                            
                            onChangePlayerBracket={(bracket_id) => {
                                setBracketSelector(bracket_id)
                            }}
                            onPlayerBracketLoad={(newpb) => {
                                setLoadPb(newpb)
                            }}
                            onPlayerBracketUpdate={(newpb, ncpbs) => {
                                setNavData({
                                    ...nav_data,
                                    active_player_bracket_id: newpb.player_bracket_id
                                })
                                setPlayData({
                                    ...play_data,
                                    player_brackets:player_brackets.filter(pb => pb.player_bracket_id != newpb.player_bracket_id).concat(newpb),
                                    competition_player_brackets: competition_player_brackets.filter(cpb => !ncpbs.find(ncpb => ncpb.competition_player_bracket_id == cpb.competition_player_bracket_id)).concat(ncpbs)
                                });
                                if(player && onCompleteBracket && newpb.completion_pct == 1){ 
                                    let cpb = ncpbs.find(cpb => cpb.player_bracket_id == newpb.player_bracket_id && cpb.bracket_competition_id == active_competition?.bracket_competition_id);
                                    onCompleteBracket(player, newpb, active_competition, cpb) 
                                }
                            }}
                            bracket_id={active_bracket_id}
                            player_bracket_id={active_player_bracket_id}
                        />                    
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1, flexDirection:'row' }}>
                <View style={{ flex:1, alignItems:mode != 'small' ? 'center' : undefined }}>
                    <View transparent style={{ flex:1, width:section_width }}>
                        <FlatList
                            data={sections}
                            keyExtractor={(item) => item}
                            refreshing={loading ? true : false}
                            key={'bracket_sections'}
                            renderItem={renderSections}
                        />
                    </View>
                    <PlayerBracketSelector
                        visible={bracket_selector?true : false}
                        bracket_id={bracket_selector}
                        leagues={leagues}
                        view_mode={view_mode}
                        insets={insets}
                        onClose={() => setBracketSelector(undefined)}
                        onSelect={(pb) => {
                            setBracketSelector(undefined)
                            setNavData({
                                ...nav_data,
                                active_player_bracket_id:pb.player_bracket_id,
                            })
                        }}
                        onCreateNew={(pb) => {
                            setBracketSelector(undefined)
                            setPlayData({
                                ...play_data,
                                player_brackets: player_brackets.concat(pb)
                            });
                            setNavData({
                                ...nav_data,
                                active_player_bracket_id: pb.player_bracket_id
                            })
                        }}
                    />
                    <Modal
                        visible={show_leaderboard.visible}
                        transparent
                        animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
                        style={{ flex:1 }}
                    >
                        <View type='blur' style={view_mode == 'desktop' ? { flex:1, justifyContent:'center', alignItems:'center' } : { flex:1, justifyContent:'flex-end', padding:10, paddingTop:(insets?.top ?? 0) + 10, paddingBottom:(insets?.bottom ?? 0)+10 }}>
                            <View float style={view_mode=='desktop'?{ height:height ?? 700, width:500 }:{ flex:1 }}>
                            <View style={{ flex:1 }}>
                                {active_competition ?
                                <CompetitionLeaderboard
                                    bracket_competition_id={active_competition.bracket_competition_id}
                                    my_competition_player_brackets={show_leaderboard.entered_cpbs}
                                    my_player_brackets={show_leaderboard.entered_pbs}
                                    view_mode={view_mode}
                                    show_champions={!BracketCompetitionHelpers.canEnter(active_competition)}
                                    can_enter={allow_enter}
                                    onClose={() => setShowLeaderboard({ visible: false, entered_pbs:[], entered_cpbs:[] })}
                                    onEnterCompetition={() => {
                                        setShowLeaderboard({ visible: false, entered_pbs:[], entered_cpbs:[] });
                                        setTimeout(() => {
                                            setShowCompetitionActions(true);
                                        }, 500);
                                    }}
                                />
                                :<></>}
                            </View>
                            </View>
                        </View>
                    </Modal>
                    <Modal
                        visible={show_competition_actions}
                        transparent
                        style={{ flex:1 }}
                        animationType={view_mode == 'desktop'?'fade':'slide'}
                    >
                        <View type='blur' style={view_mode=='desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1, justifyContent:'flex-end', padding:10, paddingTop:(insets?.top ?? 0) + 10, paddingBottom:(insets?.bottom ?? 0)+10 }}>
                            <View float style={view_mode == 'desktop'?{ height:700, width:500 }:{ flex:1 }}>
                            {active_competition ?
                                <View transparent style={{ backgroundColor:Colors.text.action, flexDirection:'row', alignItems:'center', padding:17, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                    <Image
                                        source={{ uri: active_competition.image?.url }}
                                        style={{ height:40, width:40 }}
                                        resizeMode='cover'
                                    />
                                    <View transparent style={{ flex:1, marginLeft:10 }}>
                                        <Text theme='h2' color={Colors.text.white}>{active_competition.competition_name}</Text>
                                        <Text style={{ marginTop:3 }} theme='description' color={Colors.text.white}>{active_competition.competition_description}</Text>
                                    </View>
                                                
                                    <Button
                                        type='text'
                                        float
                                        style={{ padding:10, flexDirection:'row', alignItems:'center' }}
                                        onPress={() => {
                                            setShowCompetitionActions(false)
                                            setTimeout(() => {
                                                onShareCompetition(active_competition)
                                            }, 500);
                                        }}
                                    >
                                        <Icons.ShareIcon size={14} color={Colors.text.white} />
                                    </Button>
                                </View>
                                :<></>}
                                <View style={{ flex:1 }}>
                                    
                                    {active_bracket_id ?
                                    <BracketCompetitionActions
                                        height_override={height}
                                        view_mode={view_mode}
                                        restrict_competition={restrict_competition}
                                        onShowLeaderboard={(pbs, cpbs) => {
                                            setShowCompetitionActions(false);
                                            setTimeout(() => {
                                                setShowLeaderboard({ visible: true, entered_pbs: pbs, entered_cpbs: cpbs });
                                            }, 500);
                                        }}
                                        onRequestAuthenticate={() => {
                                            setShowCompetitionActions(false);
                                            setTimeout(() => {
                                                onRequestAuthenticate(active_competition?.auth_strategy_id, active_competition?.opt_in_url)
                                            }, 500);
                                        }}
                                        player={player}
                                        hide_image
                                        onManage={onManageCompetition}
                                        onCreateNewCompetition={(bracket_id) => {
                                            if(!onCreateNewCompetition){ return }
                                            setShowCompetitionActions(false);
                                            setTimeout(() => {
                                                onCreateNewCompetition(bracket_id);
                                            }, 500);
                                        }}
                                        insets={insets}
                                        onFoundCompetition={(bc) => {
                                            setNavData({
                                                ...nav_data,
                                                active_competition:bc,
                                                active_bracket_id: bc.bracket_id
                                            })
                                        }}
                                        onSelectCompetition={async(bc) => {
                                            setNavData({
                                                ...nav_data,
                                                active_bracket_id: bc.bracket_id,
                                                active_competition: bc
                                            })
                                        }}
                                        onFocusPosition={onFocusPosition}
                                        player_brackets={player_brackets}
                                        onEnterCompetition={(new_bc, new_cpb, new_pb) => {
                                            if(new_pb){
                                                setNavData({
                                                    ...nav_data,
                                                    active_player_bracket_id: new_pb?.player_bracket_id,
                                                    active_competition:new_bc
                                                })
                                            }
                                            setPlayData({
                                                ...play_data,
                                                player_brackets: new_pb ? player_brackets.concat(new_pb) : player_brackets,
                                                competition_player_brackets: competition_player_brackets.concat(new_cpb)
                                            })
                                            if(onCompleteBracket && player && active_player_bracket && active_player_bracket.completion_pct == 1){
                                                setTimeout(() => {
                                                    onCompleteBracket(player, active_player_bracket, new_bc, new_cpb);
                                                }, 400);
                                            }
                                        }}
                                        competition_id={active_competition?.bracket_competition_id}
                                        onLeaveCompetition={(bc, rem_cpb) => {
                                            setNavData({
                                                ...nav_data,
                                                active_competition: bc
                                            });
                                            setPlayData({
                                                ...play_data,
                                                competition_player_brackets: competition_player_brackets.filter(cpb => cpb.competition_player_bracket_id != rem_cpb.competition_player_bracket_id)
                                            })
                                        }}
                                        competition_result_types={competition_result_types}
                                        bracket_id={active_bracket_id}
                                        player_balance={local_balance}
                                        player_bracket={active_player_bracket}
                                        competition_player_brackets={competition_player_brackets}
                                    /> 
                                    :<></>}
                                </View>
                                <View type='footer' style={{ flexDirection:'row', padding:10 }}>
                                    <Button
                                        style={{ flex:1 }}
                                        title='CLOSE'
                                        type='close'
                                        onPress={() => setShowCompetitionActions(false)}
                                    />
                                </View>
                            </View>
                        </View>
                    </Modal>
                    {mode == 'small' && active_bracket_id ?
                    <Button float style={{ padding:0, backgroundColor:Colors.text.action, borderBottomRightRadius:0, borderBottomLeftRadius:0 }} onPress={() => setShowCompetitionActions(true)}>
                        {active_competition ?
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <Image
                                source={{ uri: active_competition.image?.url }}
                                style={{ height:40, width:40 }}
                                resizeMode='cover'
                            />
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme='h2' color={Colors.text.white}>{active_competition.competition_name}</Text>
                                <Text style={{ marginTop:3 }} theme='description' color={Colors.text.white}>{active_competition.competition_description}</Text>
                            </View>
                            <Button
                                float
                                disabled={true}
                                type='action'
                                title='VIEW'
                                onPress={() => console.log('')}
                            />
                        </View>
                        : !restrict_competition ?
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <Icons.TrophyIcon size={20} color={Colors.text.white} />
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme='h2' color={Colors.text.white}>Available Competitions</Text>
                                <Text style={{ marginTop:3 }} theme='description' color={Colors.text.white}>Press here to see eligible competitions to join!</Text>
                            </View>
                            <Button
                                float
                                disabled={true}
                                type='action'
                                title='VIEW'
                                onPress={() => console.log('')}
                            />
                        </View>
                        :<></>}
                    </Button>
                    :<></>}
                </View>
                {mode != 'small' && active_bracket_id ?
                    <View type='header' style={{ width:300, height, overflow:'scroll', borderLeftWidth:1, borderColor:Colors.borders.light }}>
                        {active_competition ?
                        <View transparent>
                        <Image
                            source={{ uri: active_competition.image?.url }}
                            style={{ height:300, width:300 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ padding:16, flexDirection:'row', alignItems:'center', backgroundColor:Colors.text.highlight }}>
                            <View transparent style={{ flex:1, marginRight:10 }}>
                                <Text theme='h2' color={Colors.text.white}>{active_competition.competition_name}</Text>
                                <Text style={{ marginTop:3 }} theme='description' color={Colors.text.white}>{active_competition.competition_name}</Text>
                            </View>
                            <Button
                                type='text'
                                float
                                style={{ padding:10, flexDirection:'row', alignItems:'center' }}
                                onPress={() => {
                                    setShowCompetitionActions(false)
                                    setTimeout(() => {
                                        onShareCompetition(active_competition)
                                    }, 500);
                                }}
                            >
                                <Icons.ShareIcon size={14} color={Colors.text.white} />
                            </Button>
                        </View>
                    </View>
                    :<></>}
                    <View style={{ flex:1 }}>
                    <BracketCompetitionActions
                            view_mode={view_mode}
                            onRequestAuthenticate={() => onRequestAuthenticate(active_competition?.auth_strategy_id)}
                            player={player}
                            hide_image
                            restrict_competition={restrict_competition}
                            onShowLeaderboard={(pbs, cpbs) => {
                                setShowLeaderboard({ visible: true, entered_pbs:pbs, entered_cpbs: cpbs });
                            }}
                            onManage={onManageCompetition}
                            onCreateNewCompetition={(bracket_id) => {
                                if(!onCreateNewCompetition){ return }
                                setShowCompetitionActions(false);
                                setTimeout(() => {
                                    onCreateNewCompetition(bracket_id);
                                }, 500);
                            }}
                            insets={insets}
                            onFoundCompetition={(bc) => {
                                setNavData({
                                    ...nav_data,
                                    active_competition:bc,
                                    active_bracket_id: bc.bracket_id
                                })
                            }}
                            onSelectCompetition={async(bc) => {
                                setNavData({
                                    ...nav_data,
                                    active_bracket_id: bc.bracket_id,
                                    active_competition: bc
                                })
                            }}
                            onFocusPosition={onFocusPosition}
                            player_brackets={player_brackets}
                            onEnterCompetition={(new_bc, new_cpb, new_pb) => {
                                if(new_pb){
                                    setNavData({
                                        ...nav_data,
                                        active_player_bracket_id: new_pb?.player_bracket_id,
                                        active_competition:new_bc
                                    })
                                }
                                setPlayData({
                                    ...play_data,
                                    player_brackets: new_pb ? player_brackets.concat(new_pb) : player_brackets,
                                    competition_player_brackets: competition_player_brackets.concat(new_cpb)

                                })
                            }}
                            competition_id={active_competition?.bracket_competition_id}
                            onLeaveCompetition={(bc, rem_cpb) => {
                                setNavData({
                                    ...nav_data,
                                    active_competition: bc
                                });
                                setPlayData({
                                    ...play_data,
                                    competition_player_brackets: competition_player_brackets.filter(cpb => cpb.competition_player_bracket_id != rem_cpb.competition_player_bracket_id)
                                })
                            }}
                            competition_result_types={competition_result_types}
                            bracket_id={active_bracket_id}
                            player_balance={local_balance}
                            player_bracket={active_player_bracket}
                            competition_player_brackets={competition_player_brackets}
                        /> 
                    </View>
                    
                    </View>
                :<></>}
            </View>
            {loading ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                <View float style={{ width:300, height:300 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', borderTopRightRadius:8, borderTopLeftRadius:8, padding:10 }}>
                        <Text theme='h1'>Bettor Brackets</Text>
                    </View>
                    <View type='body' style={{ flex:1, justifyContent:'center' }}>
                        <ActivityIndicator size='large' color={Colors.text.h1} />
                        <Text theme='h2' style={{ marginTop:10 }} textAlign='center'>{loading}</Text>
                    </View>
                </View>
            </View>
            :<></>}
             
        </View>
    )
}

export default BracketRoom